import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import AppAppBar from "../marketing/views/AppAppBar";
import AppFooter from "../marketing/views/AppFooter";
import withRoot from "../../withRoot";
import { useQuery } from "@apollo/client";
import { GET_PUBLICATIONS } from "../../data/graphql/queries";
import { PublicationType } from "../../data/types";

const DEFAULT_IMAGE = "/images/publishing-hero.jpg";

const panelStyle = {
  position: "relative" as const,
  overflow: "hidden",
  cursor: "pointer",
  flex: 1,
  minHeight: { xs: 300, md: 450 },
  "&:hover .panel-overlay": {
    backgroundColor: "rgba(0,0,0,0.35)",
  },
};

const overlayStyle = {
  position: "absolute" as const,
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.2)",
  transition: "background-color 0.3s ease",
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "flex-end",
  alignItems: "center",
  p: 3,
  pb: 5,
};

function DigitalArchive() {
  const { data } = useQuery(GET_PUBLICATIONS, {
    variables: { paging_offset: 0, paging_limit: 100, search: "", tag_names: [], featured: true },
  });

  const featuredPublications: PublicationType[] = data?.publications ?? [];

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppAppBar />

      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 280,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src="/images/publishing-hero.jpg"
          alt="Publication Archives"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.18)",
          }}
        />
        <Typography
          variant="h2"
          sx={{
            position: "relative",
            zIndex: 2,
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: "black",
            fontSize: { xs: "2rem", sm: "2.75rem", md: "3.5rem" },
            textAlign: "center",
          }}
        >
          PUBLICATION ARCHIVES
        </Typography>
      </Box>

      {/* Two-panel section */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        {/* Digital Publications */}
        <Box
          sx={panelStyle}
          onClick={() => (window.location.href = "/publications")}
        >
          <Box
            component="img"
            src="/images/archive-digital-publications.jpg"
            alt="Digital Publications"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              minHeight: { xs: 300, md: 450 },
            }}
          />
          <Box className="panel-overlay" sx={overlayStyle}>
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: 700,
                letterSpacing: "0.1em",
                fontSize: { xs: "1.4rem", md: "1.75rem" },
                mb: 2,
                textAlign: "center",
              }}
            >
              DIGITAL PUBLICATIONS
            </Typography>
            <Button
              variant="outlined"
              href="/publications"
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: "white",
                borderColor: "white",
                "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.15)" },
              }}
            >
              VIEW
            </Button>
          </Box>
        </Box>

        {/* Inkbowl Magazine */}
        <Box sx={panelStyle}>
          <Box
            component="img"
            src="/images/archive-inkbowl-magazine.png"
            alt="Inkbowl Magazine"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top center",
              display: "block",
              minHeight: { xs: 300, md: 450 },
            }}
          />
          <Box className="panel-overlay" sx={overlayStyle}>
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: 700,
                letterSpacing: "0.1em",
                fontSize: { xs: "1.4rem", md: "1.75rem" },
                mb: 2,
                textAlign: "center",
              }}
            >
              INKBOWL MAGAZINE
            </Typography>
            <Button
              variant="outlined"
              sx={{
                color: "white",
                borderColor: "white",
                "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.15)" },
              }}
            >
              VIEW
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Featured Work */}
      {featuredPublications.length > 0 && (
        <Box sx={{ py: 6, px: { xs: 2, md: 6 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              letterSpacing: "0.1em",
              textAlign: "center",
              mb: 4,
            }}
          >
            FEATURED WORK
          </Typography>
          <Grid container spacing={2}>
            {featuredPublications.map((pub) => {
              const imgSrc = pub.coverMedia?.url || pub.coverPhotoUrl || DEFAULT_IMAGE;
              return (
                <Grid item xs={12} sm={4} key={pub.uuid}>
                  <Box
                    component="a"
                    href={`/publications/${pub.uuid}`}
                    sx={{
                      display: "block",
                      textDecoration: "none",
                      color: "inherit",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover .featured-overlay": { backgroundColor: "rgba(0,0,0,0.45)" },
                    }}
                  >
                    <Box
                      component="img"
                      src={imgSrc}
                      alt={pub.title}
                      sx={{
                        width: "100%",
                        height: 280,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <Box
                      className="featured-overlay"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.28)",
                        transition: "background-color 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "white", fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {pub.title}
                      </Typography>
                      {pub.user?.profile?.nickname && (
                        <Typography variant="caption" sx={{ color: "white" }}>
                          By {pub.user.profile.nickname}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      <Box sx={{ flexGrow: 1 }} />
      <AppFooter />
    </Box>
  );
}

export default withRoot(DigitalArchive);
